import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { User, Mail, Phone, ArrowLeft, Save, Lock, Shield, Truck, Car, Users } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        email: response.data.email || '',
        phone: response.data.phone || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/auth/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'master_admin': return <Shield className="w-5 h-5" />;
      case 'admin': return <Shield className="w-5 h-5" />;
      case 'transport': return <Truck className="w-5 h-5" />;
      case 'driver': return <Car className="w-5 h-5" />;
      case 'parent': return <Users className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const getRoleName = (role) => {
    const names = {
      master_admin: 'Master Admin',
      admin: 'Administrator',
      transport: 'Transport Operator',
      driver: 'Driver',
      parent: 'Parent'
    };
    return names[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white py-8 px-4" data-testid="profile-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="text-3xl font-heading font-bold">My Profile</h1>
        </div>

        {/* Profile Info Card */}
        <Card className="shadow-lg" data-testid="profile-card">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">{profile?.full_name}</CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  {getRoleIcon(profile?.role)}
                  {getRoleName(profile?.role)}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4" data-testid="profile-form">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="pl-10"
                    placeholder="Your full name"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    placeholder="Your email address"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10"
                    placeholder="Your phone number"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              {/* Role-specific info (read-only) */}
              {profile?.role === 'driver' && (
                <div className="space-y-4 pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground">Driver Information (read-only)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>License Number</Label>
                      <Input value={profile?.license_number || 'N/A'} disabled className="bg-slate-50" />
                    </div>
                    <div className="space-y-2">
                      <Label>PrDP Expiry</Label>
                      <Input value={profile?.prdp_expiry_date || 'N/A'} disabled className="bg-slate-50" />
                    </div>
                  </div>
                </div>
              )}

              {profile?.role === 'transport' && (
                <div className="space-y-4 pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground">Company Information (read-only)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Reg #</Label>
                      <Input value={profile?.company_registration_number || 'N/A'} disabled className="bg-slate-50" />
                    </div>
                    <div className="space-y-2">
                      <Label>SARS Tax #</Label>
                      <Input value={profile?.sars_tax_number || 'N/A'} disabled className="bg-slate-50" />
                    </div>
                  </div>
                </div>
              )}

              <Button type="submit" disabled={saving} className="w-full rounded-full" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="shadow-lg" data-testid="password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" /> Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="password-form">
              <div className="space-y-2">
                <Label>Current Password</Label>
                <PasswordInput
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <PasswordInput
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm New Password</Label>
                <PasswordInput
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" variant="outline" disabled={changingPassword} className="w-full rounded-full" data-testid="change-password-btn">
                <Lock className="w-4 h-4 mr-2" />
                {changingPassword ? 'Changing Password...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p><strong>Status:</strong> <span className={profile?.status === 'active' ? 'text-emerald-600' : 'text-red-600'}>{profile?.status}</span></p>
            <p><strong>Member Since:</strong> {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
